import { toast } from 'sonner'
import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { api } from '@/lib/api'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Template } from '@/types'
import { FileText, Plus, Trash2, Loader2, Edit3, ChevronLeft, ChevronRight, Send, Eye, Users } from 'lucide-react'

const VARIABLE_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

function extractVariables(text: string): string[] {
  const found = new Set<string>()
  let match
  while ((match = VARIABLE_RE.exec(text)) !== null) {
    found.add(match[1])
  }
  VARIABLE_RE.lastIndex = 0
  return Array.from(found)
}

function renderTemplate(text: string, values: Record<string, string>) {
  return text.replace(VARIABLE_RE, (_, name) => values[name] ?? `{{${name}}}`)
}

export function Templates() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [addOpen, setAddOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState<Template | null>(null)
  const [form, setForm] = useState({
    name: '',
    description: '',
    subject: '',
    text_body: '',
    html_body: '',
  })
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({})

  const [testOpen, setTestOpen] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testTemplate, setTestTemplate] = useState<Template | null>(null)
  const [testForm, setTestForm] = useState({
    from_email: '',
    to: '',
  })

  const [sendOpen, setSendOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendTemplate, setSendTemplate] = useState<Template | null>(null)
  const [sendForm, setSendForm] = useState({
    from_email: '',
    to: '',
  })

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null)

  const [mailboxes, setMailboxes] = useState<{ id: string; email: string; status: string }[]>([])

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/templates?page=${page}&per_page=20`)
      setTemplates(res.data.templates || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  const fetchMailboxes = async () => {
    try {
      const res = await api.get('/mailboxes?status=active&per_page=100')
      setMailboxes(res.data.mailboxes || [])
    } catch {
      // Non-fatal; test-sending will fall back to manual entry if no mailboxes load.
      setMailboxes([])
    }
  }

  useEffect(() => {
    fetchTemplates()
    fetchMailboxes()
  }, [page])

  const detectedVariables = useMemo(() => {
    const source = [form.subject, form.text_body, form.html_body].join('\n')
    return extractVariables(source)
  }, [form.subject, form.text_body, form.html_body])

  useEffect(() => {
    setSampleValues((prev) => {
      const next: Record<string, string> = {}
      for (const v of detectedVariables) {
        next[v] = prev[v] ?? ''
      }
      return next
    })
  }, [detectedVariables])

  const resetForm = () => {
    setForm({ name: '', description: '', subject: '', text_body: '', html_body: '' })
    setSampleValues({})
    setEditing(null)
  }

  const openCreate = () => {
    resetForm()
    setAddOpen(true)
  }

  const openEdit = (t: Template) => {
    setEditing(t)
    setForm({
      name: t.name,
      description: t.description || '',
      subject: t.subject,
      text_body: t.text_body || '',
      html_body: t.html_body || '',
    })
    const vars: Record<string, string> = {}
    for (const v of t.variables) {
      vars[v] = ''
    }
    setSampleValues(vars)
    setAddOpen(true)
  }

  const handleSave = async () => {
    if (!form.name || !form.subject) return
    setSaving(true)
    try {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        subject: form.subject,
        text_body: form.text_body || undefined,
        html_body: form.html_body || undefined,
      }
      if (editing) {
        await api.put(`/templates/${editing.id}`, payload)
      } else {
        await api.post('/templates', payload)
      }
      resetForm()
      setAddOpen(false)
      fetchTemplates()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this template?')) return
    try {
      await api.delete(`/templates/${id}`)
      fetchTemplates()
    } catch {
      toast.error('Failed to delete')
    }
  }

  const openTest = (t: Template) => {
    setTestTemplate(t)
    const vars: Record<string, string> = {}
    for (const v of t.variables) {
      vars[v] = ''
    }
    setSampleValues(vars)
    const activeMailbox = mailboxes.find((m) => m.status === 'active')
    setTestForm({
      from_email: activeMailbox?.email || '',
      to: '',
    })
    setTestOpen(true)
  }

  const handleTest = async () => {
    if (!testTemplate || !testForm.to || !testForm.from_email) return
    setTesting(true)
    try {
      const res = await api.post(`/templates/${testTemplate.id}/test`, {
        from_email: testForm.from_email,
        to: testForm.to,
        variables: sampleValues,
      })
      toast.success(`Test email sent to ${res.data.to} (status: ${res.data.status})`)
      setTestOpen(false)
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to send test')
    } finally {
      setTesting(false)
    }
  }

  // CHECKPOINT: PRD-04 FR-4.2.3 — Dashboard UI for POST /outbound/send-template.
  const openSend = (t: Template) => {
    setSendTemplate(t)
    const vars: Record<string, string> = {}
    for (const v of t.variables) {
      vars[v] = ''
    }
    setSampleValues(vars)
    const activeMailbox = mailboxes.find((m) => m.status === 'active')
    setSendForm({
      from_email: activeMailbox?.email || '',
      to: '',
    })
    setSendOpen(true)
  }

  const handleSend = async () => {
    if (!sendTemplate || !sendForm.to || !sendForm.from_email) return
    setSending(true)
    try {
      const toList = sendForm.to.split(',').map((e) => e.trim()).filter(Boolean)
      const res = await api.post('/outbound/send-template', {
        template_id: sendTemplate.id,
        from_email: sendForm.from_email,
        to: toList,
        variables: sampleValues,
      })
      toast.success(`Sent ${toList.length} message(s) (status: ${res.data.status})`)
      setSendOpen(false)
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to send template')
    } finally {
      setSending(false)
    }
  }

  const openPreview = (t: Template) => {
    setPreviewTemplate(t)
    const vars: Record<string, string> = {}
    for (const v of t.variables) {
      vars[v] = ''
    }
    setSampleValues(vars)
    setPreviewOpen(true)
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Email templates for your API sends</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { const t = templates[0]; if (t) openSend(t); else toast.error('Create a template first') }}>
            <Send className="mr-2 h-4 w-4" />
            Send with Template
          </Button>
          <Button onClick={openCreate} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <EmptyState
          title="No templates yet"
          description="Create reusable email templates for your API sends."
          icon={<FileText className="h-5 w-5" />}
          action={<Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New Template</Button>}
        />
      ) : (
        <>
          <div className="grid gap-3">
            {templates.map((t) => (
              <Card key={t.id} className="hover:bg-accent/20 transition-colors">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{t.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{t.subject}</p>
                      {t.description && <p className="text-xs text-muted-foreground truncate">{t.description}</p>}
                      {t.variables.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {t.variables.map((v) => (
                            <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-accent text-muted-foreground">{v}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openPreview(t)} title="Preview">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openTest(t)} title="Send test">
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSend(t)} title="Send with template">
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)} title="Edit">
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(t.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { if (!open) resetForm(); setAddOpen(open) }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* CHECKPOINT: PRD-10 FR-10.7.2 — Template form with variable extraction and sample values. */}
            <div>
              <Label htmlFor="tpl-name">Name</Label>
              <Input id="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="welcome_email" />
            </div>
            <div>
              <Label htmlFor="tpl-description">Description</Label>
              <Input id="tpl-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Sent to new users" />
            </div>
            <div>
              <Label htmlFor="tpl-subject">Subject</Label>
              <Input id="tpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Welcome, {{name}}!" />
            </div>
            <Tabs defaultValue="text">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="text">Text Body</TabsTrigger>
                <TabsTrigger value="html">HTML Body</TabsTrigger>
              </TabsList>
              <TabsContent value="text" className="mt-2">
                <Textarea
                  rows={6}
                  value={form.text_body}
                  onChange={(e) => setForm({ ...form, text_body: e.target.value })}
                  placeholder="Hi {{name}}, welcome to {{service}}."
                />
              </TabsContent>
              <TabsContent value="html" className="mt-2">
                <Textarea
                  rows={6}
                  value={form.html_body}
                  onChange={(e) => setForm({ ...form, html_body: e.target.value })}
                  placeholder="<h1>Hi {{name}}</h1><p>Welcome to {{service}}</p>"
                />
              </TabsContent>
            </Tabs>

            {detectedVariables.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">Variables & sample values</p>
                <p className="text-xs text-muted-foreground">
                  Use these sample values for test sends and preview.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {detectedVariables.map((v) => (
                    <div key={v}>
                      <Label htmlFor={`sample-${v}`} className="text-xs">{v}</Label>
                      <Input
                        id={`sample-${v}`}
                        value={sampleValues[v] || ''}
                        onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                        placeholder={`Sample ${v}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.name || !form.subject}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Test — {testTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Send a test email to verify your template renders correctly.
            </p>
            <div>
              <Label htmlFor="test-from">From email</Label>
              {mailboxes.length > 0 ? (
                <Select
                  value={testForm.from_email}
                  onValueChange={(value) => setTestForm({ ...testForm, from_email: value })}
                >
                  <SelectTrigger id="test-from">
                    <SelectValue placeholder="Select a mailbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {mailboxes.map((m) => (
                      <SelectItem key={m.id} value={m.email}>
                        {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input id="test-from" type="email" value={testForm.from_email} onChange={(e) => setTestForm({ ...testForm, from_email: e.target.value })} placeholder="No active mailboxes — type a from address" />
              )}
            </div>
            <div>
              <Label htmlFor="test-to">Recipient</Label>
              <Input id="test-to" type="email" placeholder="test@example.com" value={testForm.to} onChange={(e) => setTestForm({ ...testForm, to: e.target.value })} />
            </div>
            {testTemplate && testTemplate.variables.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">Variables</p>
                <div className="grid grid-cols-2 gap-3">
                  {testTemplate.variables.map((v) => (
                    <div key={v}>
                      <Label htmlFor={`test-var-${v}`} className="text-xs">{v}</Label>
                      <Input
                        id={`test-var-${v}`}
                        value={sampleValues[v] || ''}
                        onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                        placeholder={`Sample ${v}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={handleTest} disabled={testing || !testForm.to || !testForm.from_email}>
              {testing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send with Template Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send with Template — {sendTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Send a templated email to up to 50 recipients. Separate multiple emails with commas.
            </p>
            <div>
              <Label htmlFor="send-from">From email</Label>
              {mailboxes.length > 0 ? (
                <Select
                  value={sendForm.from_email}
                  onValueChange={(value) => setSendForm({ ...sendForm, from_email: value })}
                >
                  <SelectTrigger id="send-from">
                    <SelectValue placeholder="Select a mailbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {mailboxes.map((m) => (
                      <SelectItem key={m.id} value={m.email}>
                        {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input id="send-from" type="email" value={sendForm.from_email} onChange={(e) => setSendForm({ ...sendForm, from_email: e.target.value })} placeholder="No active mailboxes — type a from address" />
              )}
            </div>
            <div>
              <Label htmlFor="send-to">Recipients</Label>
              <Input id="send-to" type="text" placeholder="one@example.com, two@example.com" value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} />
            </div>
            {sendTemplate && sendTemplate.variables.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">Variables</p>
                <div className="grid grid-cols-2 gap-3">
                  {sendTemplate.variables.map((v) => (
                    <div key={v}>
                      <Label htmlFor={`send-var-${v}`} className="text-xs">{v}</Label>
                      <Input
                        id={`send-var-${v}`}
                        value={sampleValues[v] || ''}
                        onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                        placeholder={`Value for ${v}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending || !sendForm.to || !sendForm.from_email}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview — {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {previewTemplate && previewTemplate.variables.length > 0 && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-sm font-medium">Sample values</p>
                <div className="grid grid-cols-2 gap-3">
                  {previewTemplate.variables.map((v) => (
                    <div key={v}>
                      <Label htmlFor={`preview-var-${v}`} className="text-xs">{v}</Label>
                      <Input
                        id={`preview-var-${v}`}
                        value={sampleValues[v] || ''}
                        onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                        placeholder={`Sample ${v}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {previewTemplate && (
              <Tabs defaultValue={previewTemplate.html_body ? 'html' : 'text'}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="text">Text</TabsTrigger>
                  <TabsTrigger value="html">HTML Preview</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="mt-2">
                  <div className="rounded-md border p-3 text-sm whitespace-pre-wrap font-mono">
                    {renderTemplate(previewTemplate.text_body || previewTemplate.subject, sampleValues)}
                  </div>
                </TabsContent>
                <TabsContent value="html" className="mt-2">
                  <div className="rounded-md border p-3 text-sm">
                    <p className="text-xs text-muted-foreground mb-2">Subject: {renderTemplate(previewTemplate.subject, sampleValues)}</p>
                    <div
                      className="prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: renderTemplate(previewTemplate.html_body || `<p>${previewTemplate.text_body || ''}</p>`, sampleValues),
                      }}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
